import { useState, useCallback } from "react";
import * as XLSX from "xlsx";
import { Table, Tag, Upload, message } from "antd";
import { Button, Icon, Loading } from "~/components";

// Số di động: 0[35789]xxxxxxxx (10 số)
// Số bàn 3 số vùng: 02x xxxxxxx (10 số, VD: 024, 028)
// Số bàn 4 số vùng: 02xx xxxxxxx (11 số, VD: 0236, 0203, 0204...)
const PHONE_REGEX = /^0(\d{9}|\d{10})$/;

function cleanPhone(raw) {
	if (!raw) return '';
	// Xóa TẤT CẢ ký tự không phải số (kể cả Unicode ẩn, dấu cách đặc biệt...)
	let digits = raw.toString().replace(/\D/g, '');
	if (!digits) return '';
	// Đổi 84xxxxxxxxx → 0xxxxxxxxx
	if (digits.startsWith('84') && digits.length >= 11) {
		return '0' + digits.slice(2);
	}
	// Excel lưu SĐT dạng số nên tự cắt mất số 0 đầu.
	// VD: 0397093243 → 397093243 (9 số), 0236xxxxxxx → 236xxxxxxx (10 số).
	// Khôi phục số 0 khi chuỗi chưa bắt đầu bằng 0 và có 9 hoặc 10 chữ số.
	if (!digits.startsWith('0') && (digits.length === 9 || digits.length === 10)) {
		digits = '0' + digits;
	}
	return digits;
}

function getRowValueByIndex(sheet, rowNum, colIndex) {
	// colIndex: 0=A, 1=B, 2=C, 3=D
	const colLetter = String.fromCharCode(65 + colIndex);
	const cellAddr  = `${colLetter}${rowNum}`;
	const cell      = sheet[cellAddr];
	if (!cell) return '';
	return (cell.v !== undefined && cell.v !== null) ? cell.v.toString().trim() : '';
}

function parseSheetRows(sheet) {
	const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
	const results = [];

	// Bỏ qua dòng đầu (header), bắt đầu từ dòng 2 (index 1)
	for (let r = range.s.r + 1; r <= range.e.r; r++) {
		const excelRow = r + 1; // dòng Excel (1-based)
		const name    = getRowValueByIndex(sheet, excelRow, 0);
		const rawPhone = getRowValueByIndex(sheet, excelRow, 1);
		const address = getRowValueByIndex(sheet, excelRow, 2);
		const website = getRowValueByIndex(sheet, excelRow, 3);
		const note    = getRowValueByIndex(sheet, excelRow, 4);
		const phone   = cleanPhone(rawPhone);

		// Bỏ qua dòng hoàn toàn trống
		if (!name && !rawPhone && !address && !website && !note) continue;

		const errors = [];
		if (!name) errors.push('Tên khách/công ty không được để trống');
		if (!phone) {
			errors.push('Số điện thoại không được để trống');
		} else if (!PHONE_REGEX.test(phone)) {
			errors.push(`Số điện thoại "${rawPhone}" (sau xử lý: "${phone}") không đúng định dạng (VD: 0901234567)`);
		}

		results.push({
			rowIndex: excelRow,
			name,
			phone,
			rawPhone,
			address,
			website,
			note,
			errors,
			isValid: errors.length === 0,
		});
	}
	return results;
}

function SaleCallSourceUploadForm({ onHandleSubmit, mode = 'create' }) {

	const [rows, setRows]           = useState([]);
	const [errorRows, setErrorRows] = useState([]);
	const [loading, setLoading]       = useState(false);
	const [fileName, setFileName]     = useState('');
	const [submitting, setSubmitting] = useState(false);

	const parseExcel = useCallback((file) =>
	{
		// Reset dữ liệu file trước đó NGAY khi chọn file mới,
		// tránh trường hợp parse file mới lỗi mà bảng vẫn còn dữ liệu file cũ.
		setRows([]);
		setErrorRows([]);
		setFileName(file.name);
		setLoading(true);

		const reader = new FileReader();

		reader.onload = (e) => {
			try
			{
				const data     = new Uint8Array(e.target.result);
				const workbook = XLSX.read(data, { type: 'array' });
				const sheet    = workbook.Sheets[workbook.SheetNames[0]];

				if (!sheet) throw new Error('File Excel không có sheet dữ liệu');

				const parsed = parseSheetRows(sheet);
				const errors = parsed.filter(r => !r.isValid);

				setRows(parsed);
				setErrorRows(errors);

				if (parsed.length === 0) {
					message.warning('File không có dòng dữ liệu nào (bỏ qua dòng tiêu đề).');
				}
			}
			catch (err)
			{
				console.error(err);
				setRows([]);
				setErrorRows([]);
				message.error('Không đọc được file Excel. Vui lòng kiểm tra lại file.');
			}
			finally
			{
				setLoading(false);
			}
		};

		reader.onerror = () => {
			console.error(reader.error);
			setRows([]);
			setErrorRows([]);
			setLoading(false);
			message.error('Đọc file thất bại. Vui lòng thử lại.');
		};

		reader.readAsArrayBuffer(file);

		return false;
	}, []);

	const handleSubmit = async () =>
	{
		setSubmitting(true);

		const validRows = rows.filter(r => r.isValid);

		const contacts  = validRows.map(r => ({
			name: r.name, phone: r.phone, address: r.address, website: r.website, note: r.note
		}));

		await onHandleSubmit({ contacts });

		setSubmitting(false);
	};

	const hasData    = rows.length > 0;

	const hasErrors  = errorRows.length > 0;

	const validCount = rows.filter(r => r.isValid).length;

	const allColumns = [
		{
			title: 'Dòng',
			dataIndex: 'rowIndex',
			key: 'rowIndex',
			width: 65,
		},
		{
			title: 'Tên khách/công ty',
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: 'Số điện thoại',
			dataIndex: 'phone',
			key: 'phone',
			width: 140,
		},
		{
			title: 'Địa chỉ',
			dataIndex: 'address',
			key: 'address',
		},
		{
			title: 'Website',
			dataIndex: 'website',
			key: 'website',
		},
		{
			title: 'Ghi chú',
			dataIndex: 'note',
			key: 'note',
		},
		{
			title: 'Trạng thái',
			key: 'status',
			width: 100,
			render: (_, record) => record.isValid
				? <Tag color="green">Hợp lệ</Tag>
				: <Tag color="red">Lỗi</Tag>,
		},
	];

	const errorColumns = [
		{
			title: 'Dòng Excel',
			dataIndex: 'rowIndex',
			key: 'rowIndex',
			width: 80,
		},
		{
			title: 'Tên',
			dataIndex: 'name',
			key: 'name',
			width: 160,
			render: (v) => v || <span style={{color: '#f5222d'}}>-- trống --</span>,
		},
		{
			title: 'Số điện thoại (gốc)',
			dataIndex: 'rawPhone',
			key: 'rawPhone',
			width: 150,
			render: (v) => v || <span style={{color: '#f5222d'}}>-- trống --</span>,
		},
		{
			title: 'Nội dung lỗi',
			key: 'errors',
			render: (_, record) => (
				<ul style={{margin: 0, paddingLeft: 16}}>
					{record.errors.map((err, i) => (
						<li key={i} style={{color: '#f5222d'}}>{err}</li>
					))}
				</ul>
			),
		},
	];

	return (
		<div className="form">
			{loading && <Loading />}

			{/* Bước 1: Chọn file */}
			{!hasData && (
				<div style={{ textAlign: 'center', padding: '30px 0' }}>
					<p style={{ marginBottom: 8, fontWeight: 500 }}>
						Tải lên file Excel có các cột: <b>Tên khách/công ty</b>, <b>Số điện thoại</b>, <b>Địa chỉ</b>, <b>Website</b>, <b>Ghi chú</b>
					</p>
					<Upload
						accept=".xlsx,.xls"
						showUploadList={false}
						fileList={[]}
						beforeUpload={parseExcel}
					>
						<Button outline leftIcon={Icon.upload}>Chọn file Excel</Button>
					</Upload>
				</div>
			)}

			{/* Sau khi có dữ liệu */}
			{hasData && (
				<>
					<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
						<span>
							File: <b>{fileName}</b> &nbsp;|&nbsp;
							Tổng <b>{rows.length}</b> dòng &nbsp;|&nbsp;
							<span style={{color: '#52c41a'}}>Hợp lệ: <b>{validCount}</b></span>
							{hasErrors && <>&nbsp;|&nbsp;<span style={{color: '#f5222d'}}>Lỗi: <b>{errorRows.length}</b></span></>}
						</span>
					</div>

					{/* Nếu TẤT CẢ hợp lệ → Preview + form tạo nguồn */}
					{!hasErrors && (
						<>
							<div style={{
								background: '#f6ffed',
								border: '1px solid #b7eb8f',
								borderRadius: 6,
								padding: '10px 16px',
								marginBottom: 14,
								display: 'flex',
								alignItems: 'center',
								gap: 8,
							}}>
								<span style={{color: '#52c41a', fontSize: 18}}>{Icon.success}</span>
								<span style={{color: '#52c41a', fontWeight: 500}}>
									{mode === 'import'
										? `Tất cả ${validCount} dòng hợp lệ! Nhấn để nhập vào bộ nguồn.`
										: `Tất cả ${validCount} dòng dữ liệu đều hợp lệ! Đặt tên và nhấn tạo nguồn.`
									}
								</span>
							</div>

							<Table
								columns={allColumns}
								dataSource={rows}
								rowKey="rowIndex"
								pagination={{ pageSize: 10, showTotal: (t) => `Tổng ${t} dòng` }}
								size="small"
								style={{ marginBottom: 16 }}
							/>
							<div className="form-group d-flex justify-content-end modal-bottom gap">
								<Upload
									accept=".xlsx,.xls"
									showUploadList={false}
									fileList={[]}
									beforeUpload={parseExcel}
								>
									<Button outline leftIcon={Icon.upload} disabled={submitting}>Tải lại file</Button>
								</Upload>
								<Button
									primary
									leftIcon={submitting ? Icon.loading : Icon.plusCircle}
									onClick={handleSubmit}
									disabled={submitting}
								>
									{submitting
										? (mode === 'import' ? 'Đang nhập...' : 'Đang tạo nguồn...')
										: (mode === 'import' ? `Nhập vào nguồn (${validCount} liên hệ)` : `Tạo nguồn (${validCount} liên hệ)`)
									}
								</Button>
							</div>
						</>
					)}

					{/* Nếu CÓ LỖI → Hiển thị danh sách lỗi */}
					{hasErrors && (
						<>
							<div style={{
								background: '#fff2f0',
								border: '1px solid #ffccc7',
								borderRadius: 6,
								padding: '10px 16px',
								marginBottom: 14,
							}}>
								<span style={{color: '#f5222d', fontWeight: 500}}>
									⚠️ Có {errorRows.length} dòng dữ liệu bị lỗi. Vui lòng kiểm tra và tải lại file Excel.
								</span>
							</div>

							<p style={{fontWeight: 500, marginBottom: 8}}>Danh sách dòng lỗi:</p>
							<Table
								columns={errorColumns}
								dataSource={errorRows}
								rowKey="rowIndex"
								pagination={{ pageSize: 10 }}
								size="small"
								style={{ marginBottom: 16 }}
								rowClassName={() => 'ant-table-row-error'}
							/>

							<hr />
							<div style={{ textAlign: 'center', paddingTop: 8 }}>
								<p style={{ color: '#595959', marginBottom: 10 }}>
									Sau khi sửa file Excel, tải lại để kiểm tra lại dữ liệu
								</p>
								<Upload
									accept=".xlsx,.xls"
									showUploadList={false}
									fileList={[]}
									beforeUpload={parseExcel}
								>
									<Button primary leftIcon={Icon.upload}>Tải lại file Excel đã sửa</Button>
								</Upload>
							</div>
						</>
					)}
				</>
			)}
		</div>
	);
}

export default SaleCallSourceUploadForm;



